// AbMailDetail — Detailansicht einer E-Mail OHNE (offenes) Dokument bzw. als reiner Kontext-Blick
// (Vorbild OC-Pilot pages/EmailDetail.tsx). GET /mail/messages/:id ist fuer alle Modul-Nutzer offen
// (kein requireAdmin, s. Bauplan-Kommentar in AbShell). bodyText darf [EXTERNAL]-Zeilen roh zeigen
// (Bauplan) -> <pre>, kein HTML-Rendering (XSS-Vermeidung: reiner Text, kein dangerouslySetInnerHTML).

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Mail, FileScan } from 'lucide-react';
import { Card, SectionTitle, Chip, btnGhost, fmtDateTime, mailStatusTone } from './ui';
import { getMailMessage } from '../api';

const AbMailDetail = ({ user, isDarkMode, t, mailId, onBack, onOpenDocument, showError }) => {
  const [mail, setMail] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    getMailMessage(user, mailId)
      .then((res) => { setMail(res); setLoadError(null); })
      .catch((e) => { setLoadError(e.message); showError?.(e.message); });
  }, [user, mailId, showError]);

  useEffect(() => { setMail(null); load(); }, [load]);

  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.shell.mailDetailLoadError')}: {loadError}</p>
          <button onClick={load} className={`${btnGhost(isDarkMode)} mt-3`}>{t('ab.refresh')}</button>
        </Card>
      </div>
    );
  }
  if (!mail) {
    return <p className={`text-sm ${textMuted}`}>{t('ab.loading')}</p>;
  }

  const documents = Array.isArray(mail.documents) ? mail.documents : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <h2 className={`text-lg font-bold truncate max-w-lg ${textMain}`}>{mail.subject || t('ab.shell.mailNoSubject')}</h2>
        <Chip tone={mailStatusTone(mail.status)} isDarkMode={isDarkMode}>{t(`ab.shell.mailStatus.${mail.status}`)}</Chip>
      </div>

      <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>{t('ab.shell.mailDetailTitle')}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 ${textMuted}`}>{t('ab.shell.mailDetailSender')}</div>
            <div className={`text-sm font-medium ${textMain}`}>{mail.senderAddress || '—'}</div>
          </div>
          <div>
            <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 ${textMuted}`}>{t('ab.shell.mailDetailReceived')}</div>
            <div className={`text-sm font-medium ${textMain}`}>{fmtDateTime(mail.receivedAt)}</div>
          </div>
          <div>
            <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 ${textMuted}`}>{t('ab.shell.mailDetailStatus')}</div>
            <div className={`text-sm font-medium ${textMain}`}>{t(`ab.shell.mailStatus.${mail.status}`)}{mail.ignoreReason ? ` (${mail.ignoreReason})` : ''}</div>
          </div>
        </div>
        {mail.errorText && (
          <p className={`text-xs mt-3 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{mail.errorText}</p>
        )}
      </Card>

      {documents.length > 0 && (
        <Card isDarkMode={isDarkMode}>
          <SectionTitle isDarkMode={isDarkMode}>{t('ab.shell.mailDetailDocuments')}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {documents.map((d) => (
              <button key={d.id} type="button" onClick={() => onOpenDocument(d.id)}
                className={`text-left flex items-center gap-2.5 rounded-xl p-3 border transition-colors ${isDarkMode
                  ? 'bg-white/5 border-white/10 hover:bg-white/[0.08]' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                <FileScan className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                <span className="min-w-0">
                  <span className={`block text-sm font-medium truncate ${textMain}`}>{d.fileName}</span>
                  <span className={`block text-xs ${textMuted}`}>{t(`ab.status.${d.status}`)}</span>
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>
          <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{t('ab.shell.mailDetailBody')}</span>
        </SectionTitle>
        {mail.bodyText ? (
          <pre className={`text-xs whitespace-pre-wrap font-mono rounded-xl p-3 max-h-96 overflow-y-auto ${isDarkMode ? 'bg-black/30 text-white/70' : 'bg-slate-50 text-slate-700'}`}>
            {mail.bodyText}
          </pre>
        ) : (
          <p className={`text-sm ${textMuted}`}>{t('ab.shell.mailDetailBodyEmpty')}</p>
        )}
      </Card>
    </div>
  );
};

export default AbMailDetail;
